<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import IntegrationControlsAPI from 'dashboard/api/teletel/integrationControls';
import PageTabs from 'dashboard/components-next/teletel/PageTabs.vue';
import { useTeletelPageTabs } from 'dashboard/composables/useTeletelPageTabs';

const { integrationTabs } = useTeletelPageTabs();

const { t, locale } = useI18n();
const route = useRoute();
let scopeVersion = 0;
const kind = ref('webhook');
const enabled = ref(false);
const links = ref([]);
const events = ref([]);
const nextLink = ref(null);
const nextEvent = ref(null);
const loadingLinks = ref(false);
const loadingEvents = ref(false);
const saving = ref(null);
const fromDate = ref('');
const toDate = ref('');

async function loadLinks(beforeId = null) {
  const version = scopeVersion;
  loadingLinks.value = true;
  try {
    const { data } = await IntegrationControlsAPI.links(kind.value, {
      per_page: 25,
      before_id: beforeId || undefined,
    });
    if (version !== scopeVersion) return;
    enabled.value = data.enabled;
    links.value = data.payload.map(link => ({
      ...link,
      policy: { ...link.policy },
    }));
    nextLink.value = data.next_before_id;
  } catch {
    if (version !== scopeVersion) return;
    links.value = [];
    nextLink.value = null;
    useAlert(t('TELETEL_INTEGRATIONS.ERROR'));
  } finally {
    if (version === scopeVersion) loadingLinks.value = false;
  }
}

async function loadEvents(beforeId = null) {
  const version = scopeVersion;
  loadingEvents.value = true;
  try {
    const { data } = await IntegrationControlsAPI.events({
      per_page: 25,
      before_id: beforeId || undefined,
      from: fromDate.value
        ? new Date(`${fromDate.value}T00:00:00`).toISOString()
        : undefined,
      to: toDate.value
        ? new Date(`${toDate.value}T23:59:59.999`).toISOString()
        : undefined,
    });
    if (version !== scopeVersion) return;
    events.value = data.payload;
    nextEvent.value = data.next_before_id;
  } catch {
    if (version !== scopeVersion) return;
    events.value = [];
    nextEvent.value = null;
    useAlert(t('TELETEL_INTEGRATIONS.ERROR'));
  } finally {
    if (version === scopeVersion) loadingEvents.value = false;
  }
}

async function savePolicy(link) {
  const version = scopeVersion;
  saving.value = link.id;
  try {
    const { data } = await IntegrationControlsAPI.update(kind.value, link.id, {
      include_campaigns: link.policy.include_campaigns,
      include_delivery_read: link.policy.include_delivery_read,
    });
    if (version !== scopeVersion) return;
    Object.assign(link, data);
    useAlert(t('TELETEL_INTEGRATIONS.SAVED'));
  } catch {
    if (version !== scopeVersion) return;
    useAlert(t('TELETEL_INTEGRATIONS.ERROR'));
    await loadLinks();
  } finally {
    if (version === scopeVersion) saving.value = null;
  }
}

async function changeKind(event) {
  kind.value = event.target.value;
  await loadLinks();
}

function displayDate(value) {
  return new Date(value).toLocaleString(locale.value);
}

const stateTranslations = {
  CIRCUIT: {
    CLOSED: () => t('TELETEL_INTEGRATIONS.CIRCUIT.CLOSED'),
    OPEN: () => t('TELETEL_INTEGRATIONS.CIRCUIT.OPEN'),
    HALF_OPEN: () => t('TELETEL_INTEGRATIONS.CIRCUIT.HALF_OPEN'),
    UNKNOWN: () => t('TELETEL_INTEGRATIONS.CIRCUIT.UNKNOWN'),
  },
  EVENT_STATES: {
    ADMITTED: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.ADMITTED'),
    REJECTED: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.REJECTED'),
    FILTERED: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.FILTERED'),
    DELIVERED: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.DELIVERED'),
    EXPIRED: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.EXPIRED'),
    EXHAUSTED: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.EXHAUSTED'),
    UNCERTAIN: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.UNCERTAIN'),
    LINK_CHANGED: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.LINK_CHANGED'),
    PAYLOAD_MISSING: () =>
      t('TELETEL_INTEGRATIONS.EVENT_STATES.PAYLOAD_MISSING'),
    CIRCUIT_OPEN: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.CIRCUIT_OPEN'),
    RECOVERED: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.RECOVERED'),
    UNKNOWN: () => t('TELETEL_INTEGRATIONS.EVENT_STATES.UNKNOWN'),
  },
};

function stateLabel(group, value) {
  const key = typeof value === 'string' ? value.toUpperCase() : 'UNKNOWN';
  const translations = stateTranslations[group];
  return (translations[key] || translations.UNKNOWN)();
}

onMounted(() => {
  loadLinks();
  loadEvents();
});

watch(
  () => route.params.accountId,
  () => {
    scopeVersion += 1;
    links.value = [];
    events.value = [];
    nextLink.value = null;
    nextEvent.value = null;
    enabled.value = false;
    saving.value = null;
    loadLinks();
    loadEvents();
  }
);

onUnmounted(() => {
  scopeVersion += 1;
});
</script>

<template>
  <main class="w-full max-w-6xl p-6 overflow-auto">
    <PageTabs :tabs="integrationTabs" />
    <h1 class="text-xl font-semibold text-n-slate-12">
      {{ t('TELETEL_INTEGRATIONS.TITLE') }}
    </h1>
    <p class="mt-2 text-sm text-n-slate-11">
      {{ t('TELETEL_INTEGRATIONS.DESCRIPTION') }}
    </p>
    <p v-if="!enabled" class="mt-3 text-sm text-n-amber-11" role="status">
      {{ t('TELETEL_INTEGRATIONS.DISABLED') }}
    </p>
    <section class="mt-6">
      <div class="flex items-center gap-3">
        <h2 class="text-lg font-medium">
          {{ t('TELETEL_INTEGRATIONS.LINKS') }}
        </h2>
        <select
          :value="kind"
          :aria-label="t('TELETEL_INTEGRATIONS.TYPE')"
          :disabled="loadingLinks || saving !== null"
          @change="changeKind"
        >
          <option value="webhook">
            {{ t('TELETEL_INTEGRATIONS.WEBHOOKS') }}
          </option>
          <option value="hook">{{ t('TELETEL_INTEGRATIONS.HOOKS') }}</option>
        </select>
        <Button
          :label="t('TELETEL_INTEGRATIONS.REFRESH')"
          :disabled="loadingLinks || saving !== null"
          @click="loadLinks()"
        />
      </div>
      <p class="mt-2 text-sm text-n-slate-11">
        {{ t('TELETEL_INTEGRATIONS.LIMITS') }}
      </p>
      <p v-if="!loadingLinks && !links.length" class="mt-4">
        {{ t('TELETEL_INTEGRATIONS.EMPTY_LINKS') }}
      </p>
      <article
        v-for="link in links"
        :key="`${kind}:${link.id}`"
        class="p-4 mt-3 border rounded-lg border-n-weak"
      >
        <h3 class="font-medium">{{ link.name || `${kind}:${link.id}` }}</h3>
        <p v-if="link.policy.version === 1" class="mt-1 text-sm">
          {{ t('TELETEL_INTEGRATIONS.LEGACY') }}
        </p>
        <p v-if="!link.policy_applicable" class="mt-1 text-sm">
          {{ t('TELETEL_INTEGRATIONS.REPLY_LINK') }}
        </p>
        <template v-else>
          <p v-if="link.state?.available" class="mt-2 text-sm">
            {{
              t('TELETEL_INTEGRATIONS.PENDING', {
                count: link.state.pending_events,
                bytes: link.state.pending_bytes,
              })
            }}
            <span class="ms-2">{{
              stateLabel('CIRCUIT', link.state.circuit_state)
            }}</span>
          </p>
          <p v-else class="mt-2 text-sm">
            {{ t('TELETEL_INTEGRATIONS.STATE_UNAVAILABLE') }}
          </p>
          <div class="flex flex-wrap items-center gap-4 mt-3">
            <label class="flex items-center gap-2 text-sm">
              <input
                v-model="link.policy.include_campaigns"
                type="checkbox"
                :disabled="!enabled || saving !== null"
              />
              {{ t('TELETEL_INTEGRATIONS.INCLUDE_CAMPAIGNS') }}
            </label>
            <label class="flex items-center gap-2 text-sm">
              <input
                v-model="link.policy.include_delivery_read"
                type="checkbox"
                :disabled="!enabled || saving !== null"
              />
              {{ t('TELETEL_INTEGRATIONS.INCLUDE_DELIVERY_READ') }}
            </label>
            <Button
              :label="t('TELETEL_INTEGRATIONS.SAVE')"
              :disabled="!enabled || saving !== null || loadingLinks"
              :is-loading="saving === link.id"
              @click="savePolicy(link)"
            />
          </div>
        </template>
      </article>
      <Button
        v-if="nextLink"
        class="mt-3"
        :label="t('TELETEL_INTEGRATIONS.OLDER')"
        :disabled="loadingLinks || saving !== null"
        @click="loadLinks(nextLink)"
      />
    </section>
    <section class="mt-8">
      <h2 class="text-lg font-medium">
        {{ t('TELETEL_INTEGRATIONS.EVENTS') }}
      </h2>
      <p class="mt-2 text-sm text-n-slate-11">
        {{ t('TELETEL_INTEGRATIONS.EVENTS_HELP') }}
      </p>
      <div class="flex flex-wrap items-end gap-3 mt-3">
        <label>
          {{ t('TELETEL_INTEGRATIONS.FROM') }}
          <input v-model="fromDate" type="date" :disabled="loadingEvents" />
        </label>
        <label>
          {{ t('TELETEL_INTEGRATIONS.TO') }}
          <input v-model="toDate" type="date" :disabled="loadingEvents" />
        </label>
        <Button
          :label="t('TELETEL_INTEGRATIONS.REFRESH')"
          :disabled="loadingEvents"
          @click="loadEvents()"
        />
      </div>
      <p v-if="!loadingEvents && !events.length" class="mt-4">
        {{ t('TELETEL_INTEGRATIONS.EMPTY_EVENTS') }}
      </p>
      <ul class="mt-3 divide-y divide-n-weak">
        <li v-for="event in events" :key="event.id" class="py-3 text-sm">
          <time :datetime="event.created_at">
            {{ displayDate(event.created_at) }}
          </time>
          <span class="mx-2">{{ event.metadata.link_key }}</span>
          {{ stateLabel('EVENT_STATES', event.metadata.state) }}
          <span v-if="event.metadata.id" class="block mt-1 font-mono text-xs">
            {{ event.metadata.id }}
          </span>
        </li>
      </ul>
      <Button
        v-if="nextEvent"
        class="mt-3"
        :label="t('TELETEL_INTEGRATIONS.OLDER')"
        :disabled="loadingEvents"
        @click="loadEvents(nextEvent)"
      />
    </section>
  </main>
</template>
